import { Card, CardContent, Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Snackbar, Typography, Autocomplete, TextField } from '@mui/material';
import { Alert } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useMemo } from 'react';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import Form from '../../../components/general/form';
import { ButtonAccept, ButtonCancel, ButtonOk } from '../../../components/general/mini_components';
import Loader from '../../../components/Loader';
import { IDepartment, IUnit, SnackbarTypeSeverity } from '../../../constants/types';
import { useSnackBarState, SnackbarType, useUnitSelector, useDeparmentsSelector, useProfileInfo } from '../../../hooks';
import { getCurrentResearcherUuid, requestTypeToTranslation } from '../../../utils/index.jsx';
import { TabsSherwood } from '../../components/Tabs';
import { IRequest, IServiceInvestigation, RequestType, ServiceType } from './types';
import ICTSelectorGeneral from '../../../components/general/SmartFields/ICT';
import { Diagnosis, SmartFieldType } from '../../../components/general/SmartFields';
import { QuillWrapper } from '../../../components/general/FieldSherwood';
import ReactQuill from 'react-quill';
import { CheckCircle } from '@mui/icons-material';

interface RequestFormProps {
    serviceType: ServiceType;
    uuidPatient:string,
    uuidInvestigation:string,
    units:IUnit[],
    initServicesInvestigation?: IServiceInvestigation[],
    cancel?:() => void,
    callBackRequestFinished:(requestsService:IRequest)=>void,  
}

export interface SurgeryRequestData {
    ictCode?: string; // ICT code for surgery reason
    surgicalIndication?: string; // Text description of the reason for the surgery
}


const RequestForm: React.FC<RequestFormProps> = ({ uuidPatient, units, serviceType, uuidInvestigation, initServicesInvestigation, callBackRequestFinished, cancel }) => {
    const [loading, setLoading] = React.useState(false);
    const [snackbar, setShowSnackbar] = useSnackBarState();
    const [servicesInvestigation, setServicesInvestigation] = React.useState<null | IServiceInvestigation[]>(initServicesInvestigation ? initServicesInvestigation : null);
    const [request, setRequest] = React.useState<null | IRequest>(null);
    const {profile, loadingProfile} = useProfileInfo();

    function handleClose(){
        setShowSnackbar({show:false});
        if(snackbar.severity === SnackbarTypeSeverity.SUCCESS && request !== null){
            callBackRequestFinished(request);
        }
    }

    
    function makeRequest(uuidPatient:string, servicesInvestigation:number[], uuidDepartment:string | null, ictCode: SurgeryRequestData| null = null) {
        setLoading(true);
        const postObject = {
            servicesInvestigationId: servicesInvestigation,
            uuidPatientInvestigation:uuidPatient,
            typeRequest: serviceType === ServiceType.SURGERY ? RequestType.SURGERY : RequestType.SERVICES,
            uuidDepartment:uuidDepartment,
            typeSpecificData: ictCode,
        }
        axios.post(import.meta.env.VITE_APP_API_URL+"/hospital/"+uuidInvestigation+"/service/request", postObject, { headers: {"Authorization" : localStorage.getItem("jwt") }})
        .then(response => {
            if(response.status === 200){
                let message = "pages.hospital.services.success";
                if(serviceType === ServiceType.SURGERY){
                    message = "pages.hospital.services.success_surgery";
                }
                setShowSnackbar({show:true, severity:"success", message:message});
                setRequest(response.data.request);
            }
            else{
                setShowSnackbar({show:true, severity:"error", message:"general.error"});
            }
            setLoading(false);
        })
        .catch(error => {
            console.log(error.response.data.errorCode);
            let translateError = "general.error";
            if(error.response.data.errorCode === 3){
                translateError = "pages.hospital.services.error.previous_request";
            }
            setLoading(false);
            setShowSnackbar({show:true, severity:"error", message:translateError});
          
        })
    }
    useEffect(() => {
        if(!servicesInvestigation){
            setLoading(true);
            axios(import.meta.env.VITE_APP_API_URL+"/hospital/"+uuidInvestigation+"/services/"+serviceType, { headers: {"Authorization" : localStorage.getItem("jwt") }})
            .then(response => {
                setServicesInvestigation(response.data.servicesInvestigation);
                setLoading(false);
            })
        }

    }, []);

    if(loadingProfile){
        return <Loader />;
    }
    return <RequestFormCoreLocalized loading={loading} snackbar={snackbar} servicesInvestigation={servicesInvestigation ? servicesInvestigation : []}
                handleCloseSnackBar={handleClose} cancel={cancel} units={units} serviceType={serviceType}
                uuidResearcher={profile.uuid}
                callBackFormSubmitted={(servicesInvestigation:number[], uuidDepartment:string | null, ictCode: SurgeryRequestData| null = null) => 
                    makeRequest(uuidPatient, servicesInvestigation, uuidDepartment, ictCode)} />;
}

export default RequestForm;

interface RequestFormCoreProps extends Omit<RequestFormProps, 'uuidPatient' | 'uuidInvestigation' | 'callBackRequestFinished' > , LocalizeContextProps {
    loading: boolean;
    snackbar: SnackbarType;
    uuidResearcher:string,
    servicesInvestigation:IServiceInvestigation[],
    handleCloseSnackBar:()=>void,
    callBackFormSubmitted:(serviceInvestigation:number[], uuidDepartment:string | null, ictCode?: SurgeryRequestData| null) => void
}

// Add TypeScript declaration for import.meta.env
declare global {
    interface ImportMeta {
        env: {
            VITE_APP_API_URL: string;
            [key: string]: string;
        };
    }
}

export const RequestFormCore: React.FC<RequestFormCoreProps> = ({ loading, servicesInvestigation, snackbar, uuidResearcher, serviceType, units, activeLanguage,
                                                                    translate, callBackFormSubmitted, handleCloseSnackBar, cancel }) => {
    const [servicesInvestigationSelected, setServicesInvestigationSelected] = React.useState<{ [id: string] : boolean; }>({});
    const [errorServices, setErrorServices] = React.useState(false);
    const [formSubmitted, setFormSubmitted] = React.useState(false);
    const [selectedIctCode, setSelectedIctCode] = React.useState<string | null>(null);
    const [surgeryIndication, setSurgeryIndication] = React.useState<string | null>(null);
    const [ictCodeError, setIctCodeError] = React.useState(false);
    const [notes, setNotes] = React.useState<string | null>(null);

    // SHER-446: pre-fill the requesting department from the doctor's own service.
    // Pass the current researcher + onlyResearcherIfSingle so that a doctor in a
    // single department gets it auto-selected and hidden; doctors in zero or
    // several departments keep the full hospital list (unchanged behaviour).
    const {renderDepartmentSelector, departmentSelected, markAsErrorDepartmentCallback} = useDeparmentsSelector(false, getCurrentResearcherUuid(), true, undefined, true);

    useEffect(() => {
        if(!loading){
            setFormSubmitted(false);
        }
    }, [loading]);

    const getServiceByCategory = (category:string | undefined) => {
        const filterServicesInvestigation = category ? servicesInvestigation.filter((serviceInv) => serviceInv.service.category === category) : servicesInvestigation;
        return filterServicesInvestigation.map((serviceInv) => {
            return {
                ...serviceInv,
                service: {
                    ...serviceInv.service,
                }
               }           })
    }
    const serviceCategories = useMemo(() => {
        let categories:{[id:string]:IServiceInvestigation[]} = {};
        servicesInvestigation.forEach(serviceInvestigation => {
            if(!categories[serviceInvestigation.service.category]){
                categories[serviceInvestigation.service.category] = [];
            }
            categories[serviceInvestigation.service.category].push(serviceInvestigation);
        });
        return categories;
    }, [servicesInvestigation]);
    const typeTestString = requestTypeToTranslation(serviceType);
    const SERVICE_SEPARATOR = "service_";
    
    function renderIctSelector() {
        return (
            <Grid
                item
                xs={12}
                sx={{
                    width: '100%',
                    // ICTSelectorGeneral wraps its input in a <Grid container spacing={3}>,
                    // whose negative margin indents the field and makes it render narrower
                    // than the sibling department/service selectors. Neutralise that inset so
                    // the three surgical-request fields share the same full width.
                    '& .MuiGrid-container': { margin: 0, width: '100%' },
                    '& .MuiGrid-container > .MuiGrid-item': { paddingTop: 0, paddingLeft: 0 },
                }}
            >
                <ICTSelectorGeneral
                    variant="outlined"
                    size="lg"
                    language={activeLanguage.code}
                    typeMargin="normal"
                    type="ICT"
                    label={translate("pages.hospital.services.request.surgery_indication").toString()}
                    elementSelected={(element: SmartFieldType) => {
                        if ('ict' in element && 'ict-code' in element) {
                            setSelectedIctCode(element['ict-code'] as string);
                            setSurgeryIndication(element['ict'] as string);
                            if (element['ict-code']) setIctCodeError(false);
                        }
                    }}
                    error={ictCodeError}
                />
            </Grid>
        );
    }
  
    function callBackForm(){
        const serviceInvestigationIds = Object.keys(servicesInvestigationSelected).filter((key) => servicesInvestigationSelected[key] === true).map((key) => parseInt(key.replace(SERVICE_SEPARATOR, "")));
        
        // Check if we need to validate ICT code (only for surgery)
        const needIctCode = serviceType === ServiceType.SURGERY;
        const ictCodeValid = !needIctCode || (needIctCode && selectedIctCode !== null);
        
        if((departmentSelected === null && units.length > 0) || serviceInvestigationIds.length === 0 || !ictCodeValid){
            if(departmentSelected === null){
                markAsErrorDepartmentCallback();
            }
            if(serviceInvestigationIds.length === 0){
                setErrorServices(true);  
                setFormSubmitted(false); 
            }
            if(needIctCode && !ictCodeValid){
                setIctCodeError(true);
            }
            return;
        }
    
        console.log("callBackForm", servicesInvestigationSelected);
        setFormSubmitted(true);
        console.log("serviceInvestigationIds", serviceInvestigationIds);
        let extraParams = null;
        if(serviceType === ServiceType.SURGERY){
            extraParams = {
                ictCode: selectedIctCode,
                ict: surgeryIndication,
            }
        }
        else{
            extraParams = {
                notes: notes
            }
        }
        callBackFormSubmitted(serviceInvestigationIds, departmentSelected, extraParams);
    }

    function renderServiceCheckboxes(category?:string){
        const services = getServiceByCategory(category);
        return services.sort((serviceA, serviceB) => {
            const labelA = serviceA.description.toString();
            const labelB = serviceB.description.toString();
            return labelA.localeCompare(labelB, activeLanguage.code);
        }).map((serviceInvestigation) => {
                return <Grid item xs={6}>
                        <FormControlLabel
                            control={<Checkbox checked={servicesInvestigationSelected[SERVICE_SEPARATOR+serviceInvestigation.id]} />}
                            onChange={
                                (event: React.ChangeEvent<{}>, checked: boolean) => {
                                    setServicesInvestigationSelected({...servicesInvestigationSelected, ["service_"+serviceInvestigation.id]:checked});
                                }
                            }
                            label={serviceInvestigation.description} 
                        />
                        </Grid>
        })
    }

    function renderServiceAutocomplete(category?: string) {
        let services = getServiceByCategory(category);
        const sortedServiceInvestigation = services.sort((serviceA, serviceB) => {
            const labelA = serviceA.description.toString();
            const labelB = serviceB.description.toString();
            return labelA.localeCompare(labelB, activeLanguage.code);
        });
        return (
            <Grid item xs={12}>
                <Autocomplete
                    multiple
                    options={sortedServiceInvestigation}
                    getOptionLabel={(option) => option.description}
                    onChange={(event, newValue) => {
                        const newSelected = { ...servicesInvestigationSelected };
                        // Reset all selections
                        services.forEach(service => {
                            newSelected[SERVICE_SEPARATOR + service.id] = false;
                        });
                        // Set new selections
                        newValue.forEach(service => {
                            newSelected[SERVICE_SEPARATOR + service.id] = true;
                        });
                        setServicesInvestigationSelected(newSelected);
                    }}
                    value={services.filter(service => servicesInvestigationSelected[SERVICE_SEPARATOR + service.id])}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            error={errorServices}
                            variant="outlined"
                            label={<Translate id="pages.hospital.services.request.select_services_surgery" />}
                        />
                    )}
                />
            </Grid>
        );
    }
    
    function renderTabsServices(){
        if(Object.values(serviceCategories).length === 1){
            return(
                <Grid container item xs={12} spacing={1}>
                    { renderServiceCheckboxes() }
                </Grid>
            )
        }
        else{
            const orderedLabels = Object.keys(serviceCategories).map((category)=> {return {
                label : translate(`pages.hospital.${typeTestString}.category.${category.toLocaleLowerCase()}`).toString() ,
                key: category
            }}).sort((labelA, labelB) => labelA.label.localeCompare(labelB.label, activeLanguage.code))
            return (
                <TabsSherwood name="Requests" whiteBackground={true}
                    labels={orderedLabels.map((orderedLabel) => orderedLabel.label) } >
                        { orderedLabels.map((orderedLabel) => orderedLabel.key).map((serviceCategory) => {
                            return <Grid container item xs={12} spacing={1}>
                                { renderServiceCheckboxes(serviceCategory) }
                            </Grid>
                        }) }
                        <></>
                </TabsSherwood>
            ); 
        }
    }

    function renderNotes(){
        return(
            <Grid item xs={12} paddingTop={3}>
                 <Typography variant="body2" gutterBottom>
                    <Translate id="pages.hospital.services.request.notes" />:
                </Typography>
                <QuillWrapper >
                    <ReactQuill
                        style={{ fontSize: "24px" }}
                        value={notes || ""}
                        onChange={(newValue, delta, source) => {
                        if (source === "user") {
                            setNotes(newValue);
                        }
                        }}
                        onBlur={(range, source, quill) => {
                            setNotes(quill.getHTML());
                        }}
                    />
                </QuillWrapper>
            </Grid>
        )
    }
    function renderCore(){
        if(serviceType === ServiceType.SURGERY){
            return(
                <div className="ict-surgery-container" style={{ width: '100%' }}>
                    {renderDepartmentSelector()}
                    {renderIctSelector()}
                    {renderServiceAutocomplete()}
                </div>
            ) 
        }
        else{
            return(
                <>
                    {renderDepartmentSelector()}
                    {renderTabsServices()}
                    {renderNotes()}
                </>
            ) 
        }
    }

    if(loading){
        return <Loader />;
    }

    return (
        <>  
            <Snackbar
                anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
                }}
                open={snackbar.show}
                autoHideDuration={4000}
                onClose={handleCloseSnackBar}
                >
                    <Alert severity={snackbar.severity}>
                            <Translate id={snackbar.message} />                            
                        </Alert>
                </Snackbar>
                <Grid container spacing={3}>
                    <Grid xs={12} item>
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle1" ><Translate id={`hospital.request.${requestTypeToTranslation(serviceType)}`} /></Typography>
                                {
                                    renderCore()
                                }
                            </CardContent>
                        </Card>
                </Grid>
                {
                    errorServices && serviceType !== ServiceType.SURGERY &&
                    <Grid item xs={12}>
                        <Alert severity="error">
                            <Translate id="pages.hospital.services.request.select_services" />
                        </Alert>
                    </Grid>
                }
                <Grid container item xs={12}>
                    <Grid item>
                        <ButtonOk disabled={formSubmitted || loading || snackbar.show} onClick={callBackForm}><Translate id="pages.hospital.services.request.make_request" /></ButtonOk> 
                    </Grid>
                    <Grid item style={{marginLeft:"1rem"}}>
                        <ButtonCancel disabled={loading || snackbar.show} onClick={cancel} ><Translate id="general.cancel" /></ButtonCancel>
                    </Grid>
                </Grid>   
            </Grid>
        </>
    );
};

export const RequestFormCoreLocalized = withLocalize(RequestFormCore)
